import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Tooltip, Collapse, ButtonBase } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colors } from '../theme';

/**
 * Chart primitives for the Dashboard → "Deal metrics" tab.
 *
 * Hand-rolled SVG rather than @mui/x-charts because both forms need per-mark
 * control the library doesn't expose: a histogram needs bin-EDGE x labels plus
 * a median rule, and the bubble scatter needs a per-point radius (x-charts'
 * ScatterChart takes a single markerSize for the whole series). Hand-rolling
 * also sidesteps the ResizeObserver sizing traps that bit the velocity chart.
 *
 * House rules applied consistently across every chart here:
 *   - One hue per chart. Each chart is a single series, so there is no legend
 *     to read — the card title names the measure. Colour is never used to
 *     re-encode magnitude the bar length already shows.
 *   - Thin marks: bars capped at 32px, 4px rounded data-end, square baseline.
 *   - White does the separating: a 2px surface gap between adjacent bars, a
 *     2px surface ring on overlapping bubbles. Never a border around a mark.
 *   - Gridlines/axes are solid hairlines one step off the surface — never
 *     dashed, never competing with the data.
 *   - No number printed on every mark. The axis, one median rule, and the
 *     hover tooltip carry values — and every chart has a table-view twin, so
 *     nothing is reachable ONLY by hovering.
 */

// --------------------------------------------------------------------------- //
// Formatting + small stats helpers
// --------------------------------------------------------------------------- //
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

/** Trim trailing zeros so a bin edge reads "17.5%" and "20%", never "20.0%". */
const trim = (n: number, dp: number) => String(Number(n.toFixed(dp)));

export const fmtMoney = (n: number) => `$${compact.format(n)}`;
export const fmtPctPoint = (n: number) => `${trim(n, 1)}%`;
export const fmtCapPct = (n: number) => `${trim(n, 2)}%`;
export const fmtMoic = (n: number) => `${trim(n, 2)}x`;

export const median = (xs: number[]): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** 1 / 2 / 2.5 / 5 / 10 × 10^k — the step sizes people read without effort. */
const niceStep = (raw: number): number => {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return step * mag;
};

export interface Bin { x0: number; x1: number; count: number; overflow?: boolean }

/** Linear-interpolated quantile over an ALREADY-SORTED array. */
const quantile = (sorted: number[], q: number): number => {
  if (!sorted.length) return NaN;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
};

/**
 * Equal-width bins on nice boundaries. Bin edges land on round numbers so the
 * x axis reads as a number line ("$0 / $10M / $20M"), not as opaque buckets.
 *
 * Heavy right tails get an OVERFLOW bin. One 50x deal in a book whose other 36
 * sit under 5x will otherwise own the whole axis and flatten every bar that
 * matters — technically correct, practically unreadable. So when the tail runs
 * far past the 90th percentile, the axis stops at a round number near p90 and
 * everything above is collected into a final bin labelled "10x+".
 *
 * The outliers are still counted and still visible — they just stop setting the
 * scale. Nothing is silently dropped: the chart prints what was folded in.
 */
export function buildBins(values: number[], targetBins = 8, clampTail = true): Bin[] {
  const vals = values.filter((v) => Number.isFinite(v));
  if (!vals.length) return [];
  const sorted = [...vals].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  // Scale-free tail test, so it behaves the same for MOIC (2x) and price ($20M)
  // and survives negative values (an IRR book running from -100%).
  let axisMax = max;
  let clamped = false;
  if (clampTail && sorted.length >= 8) {
    const p90 = quantile(sorted, 0.9);
    const body = p90 - min;
    if (body > 0 && (max - min) > body * 1.8) {
      axisMax = p90;
      clamped = true;
    }
  }

  // All deals at the same value still get a real (single) bin on the number line.
  const spread = axisMax - min;
  const step = niceStep((spread || Math.abs(axisMax) || 1) / targetBins);
  const start = Math.floor(min / step) * step;
  // A max sitting exactly on a boundary belongs to the bin below it, not to a
  // new empty one — hence the epsilon before the ceil.
  const count = Math.max(1, Math.ceil((axisMax - start) / step - 1e-9));

  const bins: Bin[] = Array.from({ length: count }, (_, i) => ({
    x0: start + i * step,
    x1: start + (i + 1) * step,
    count: 0,
  }));

  const edge = start + count * step;
  let over = 0;
  vals.forEach((v) => {
    if (clamped && v > edge) { over += 1; return; }
    const idx = Math.min(count - 1, Math.max(0, Math.floor((v - start) / step)));
    bins[idx].count += 1;
  });

  // Only add the overflow bin if it actually caught something — a clamp that
  // folds in zero deals is just a shorter axis, and the label would lie.
  if (clamped && over > 0) bins.push({ x0: edge, x1: max, count: over, overflow: true });
  return bins;
}

// --------------------------------------------------------------------------- //
// Responsive width — SVG needs a real pixel width, and a viewBox stretch would
// distort the type. One ResizeObserver per chart, no layout thrash.
// --------------------------------------------------------------------------- //
export function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

// --------------------------------------------------------------------------- //
// Card shell + the table-view twin every chart ships with
// --------------------------------------------------------------------------- //
export const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  meta?: string;
  table?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, meta, table, children }) => {
  const [showTable, setShowTable] = useState(false);
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}</Typography>
        {meta && (
          <Typography variant="caption" sx={{ color: colors.grey[600], flexShrink: 0, whiteSpace: 'nowrap' }}>
            {meta}
          </Typography>
        )}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1.5 }}>{subtitle}</Typography>
      )}
      <Box sx={{ mt: subtitle ? 0 : 1.5 }}>{children}</Box>
      {table && (
        <>
          <Box sx={{ mt: 1 }}>
            <ButtonBase
              onClick={() => setShowTable((s) => !s)}
              aria-expanded={showTable}
              sx={{
                fontSize: '0.75rem', fontWeight: 600, color: colors.blueDark,
                px: 0.75, py: 0.5, ml: -0.75, borderRadius: 1,
                '&:hover': { bgcolor: colors.blueTint },
              }}
            >
              {showTable ? 'Hide values' : 'View values'}
            </ButtonBase>
          </Box>
          <Collapse in={showTable} unmountOnExit>
            <Box sx={{ mt: 1, maxHeight: 260, overflowY: 'auto' }}>{table}</Box>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

/** Compact, scannable table twin — the WCAG-clean equivalent of the chart. */
export const DataTable: React.FC<{ head: string[]; rows: (string | number)[][] }> = ({ head, rows }) => (
  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
    <Box component="thead">
      <Box component="tr">
        {head.map((h, i) => (
          <Box
            component="th"
            key={h}
            sx={{
              textAlign: i === 0 ? 'left' : 'right',
              color: colors.grey[600], fontWeight: 700, fontSize: '0.6875rem',
              py: 0.75, borderBottom: `1px solid ${colors.grey[300]}`,
              position: 'sticky', top: 0, bgcolor: colors.white,
            }}
          >
            {h}
          </Box>
        ))}
      </Box>
    </Box>
    <Box component="tbody">
      {rows.map((r, ri) => (
        <Box component="tr" key={ri} sx={{ '&:hover': { bgcolor: colors.grey[50] } }}>
          {r.map((c, ci) => (
            <Box
              component="td"
              key={ci}
              sx={{
                textAlign: ci === 0 ? 'left' : 'right',
                py: 0.6, borderBottom: `1px solid ${colors.grey[300]}`,
                fontVariantNumeric: ci === 0 ? 'normal' : 'tabular-nums',
                color: colors.grey[900],
              }}
            >
              {c}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  </Box>
);

export const EmptyNote: React.FC<{ text: string; height?: number }> = ({ text, height = 200 }) => (
  <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 2 }}>
    <Typography variant="body2" sx={{ color: colors.grey[600] }}>{text}</Typography>
  </Box>
);

// --------------------------------------------------------------------------- //
// Histogram
// --------------------------------------------------------------------------- //
const AXIS_TEXT = { fontSize: 11, fill: colors.grey[600] } as const;

/** Rounded data-end, square baseline — the bar grows out of the axis. */
const barPath = (x: number, y: number, w: number, h: number, r = 4) => {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} `
    + `L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
};

export const Histogram: React.FC<{
  bins: Bin[];
  format: (n: number) => string;
  medianValue?: number | null;
  hue?: string;
  height?: number;
  ariaLabel?: string;
}> = ({ bins, format, medianValue = null, hue = colors.blue, height = 236, ariaLabel }) => {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const pad = { top: 22, right: 10, bottom: 32, left: 34 };
  const plotW = Math.max(0, width - pad.left - pad.right);
  const plotH = Math.max(0, height - pad.top - pad.bottom);

  const total = bins.reduce((a, b) => a + b.count, 0);
  const maxCount = Math.max(1, ...bins.map((b) => b.count));
  const yStep = Math.max(1, niceStep(maxCount / 3));
  const yMax = Math.ceil(maxCount / yStep) * yStep;
  const yTicks: number[] = [];
  for (let t = 0; t <= yMax + 1e-9; t += yStep) yTicks.push(t);

  const slot = bins.length ? plotW / bins.length : 0;
  const barW = Math.max(2, Math.min(32, slot - 2));   // 2px surface gap between bars
  const xOf = (i: number) => pad.left + i * slot + (slot - barW) / 2;
  const yOf = (c: number) => pad.top + plotH - (c / yMax) * plotH;

  // The overflow bin sits on the axis but not on its scale, so it is excluded
  // from edge labelling and separated by a hairline break rule.
  const overflowBin = bins.find((b) => b.overflow);
  const normal = bins.filter((b) => !b.overflow);

  // Label bin EDGES (a number line), thinning until labels can't collide.
  const edges = normal.length ? [normal[0].x0, ...normal.map((b) => b.x1)] : [];
  const every = Math.max(1, Math.ceil(edges.length / Math.max(2, Math.floor(plotW / 68))));
  const edgeX = (i: number) => pad.left + i * slot;

  // Position a value by the bin that contains it, not by a linear map across
  // the whole domain — with an overflow bin the domain is no longer linear.
  const xForValue = (v: number): number | null => {
    for (let i = 0; i < bins.length; i += 1) {
      const b = bins[i];
      const last = i === bins.length - 1;
      if (v >= b.x0 && (v < b.x1 || last)) {
        const span = b.x1 - b.x0 || 1;
        return pad.left + (i + Math.min(1, (v - b.x0) / span)) * slot;
      }
    }
    return null;
  };
  const medX = medianValue != null && bins.length ? xForValue(medianValue) : null;

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel} style={{ display: 'block', overflow: 'visible' }}>
          {/* gridlines — solid hairlines, one step off the surface */}
          {yTicks.map((t) => (
            <g key={t}>
              <line
                x1={pad.left} x2={pad.left + plotW} y1={yOf(t)} y2={yOf(t)}
                stroke={t === 0 ? colors.grey[400] : colors.grey[300]} strokeWidth={1}
              />
              <text x={pad.left - 8} y={yOf(t) + 4} textAnchor="end" {...AXIS_TEXT} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {t}
              </text>
            </g>
          ))}

          {/* bars */}
          {bins.map((b, i) => {
            const h = (b.count / yMax) * plotH;
            const active = hover === i;
            return (
              <Tooltip
                key={`${b.x0}-${i}`}
                arrow
                followCursor
                title={
                  <span>
                    {b.overflow ? `${format(b.x0)} and above` : `${format(b.x0)} – ${format(b.x1)}`}
                    <br />
                    {b.count} deal{b.count === 1 ? '' : 's'}
                    {total > 0 ? ` · ${Math.round((b.count / total) * 100)}%` : ''}
                    {b.overflow ? <><br />{`highest ${format(b.x1)}`}</> : null}
                  </span>
                }
              >
                <g
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'default' }}
                >
                  {/* full-height hit target so a 1-deal bin is still easy to hover */}
                  <rect x={pad.left + i * slot} y={pad.top} width={slot} height={plotH} fill="transparent" />
                  {b.count > 0 && (
                    <path
                      d={barPath(xOf(i), yOf(b.count), barW, h)}
                      fill={active ? colors.blueDark : hue}
                      style={{ transition: 'fill .15s ease' }}
                    />
                  )}
                </g>
              </Tooltip>
            );
          })}

          {/* median rule — the one direct label the chart carries */}
          {medX != null && (
            <g>
              <line x1={medX} x2={medX} y1={pad.top - 6} y2={pad.top + plotH}
                stroke={colors.white} strokeWidth={3} />
              <line x1={medX} x2={medX} y1={pad.top - 6} y2={pad.top + plotH}
                stroke={colors.grey[700]} strokeWidth={1} />
              <text
                x={Math.min(Math.max(medX, pad.left + 2), pad.left + plotW - 2)}
                y={pad.top - 11}
                textAnchor={medX > pad.left + plotW * 0.75 ? 'end' : medX < pad.left + plotW * 0.25 ? 'start' : 'middle'}
                style={{ fontSize: 11, fontWeight: 700, fill: colors.grey[700] }}
              >
                Median {format(medianValue as number)}
              </text>
            </g>
          )}

          {/* x axis — bin edges (overflow bin excluded: it has no scale) */}
          {edges.map((e, i) => {
            const isLast = i === edges.length - 1;
            if (i % every !== 0 && !isLast) return null;
            if (isLast && i % every !== 0 && (i % every) < every / 2) return null;
            return (
              <text
                key={i}
                x={edgeX(i)}
                y={pad.top + plotH + 18}
                textAnchor={i === 0 ? 'start' : isLast && !overflowBin ? 'end' : 'middle'}
                {...AXIS_TEXT}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {format(e)}
              </text>
            );
          })}

          {/* axis break + label for the overflow bar */}
          {overflowBin && (
            <g>
              <line
                x1={pad.left + normal.length * slot}
                x2={pad.left + normal.length * slot}
                y1={pad.top}
                y2={pad.top + plotH + 6}
                stroke={colors.grey[300]}
                strokeWidth={1}
              />
              <text
                x={pad.left + (normal.length + 0.5) * slot}
                y={pad.top + plotH + 18}
                textAnchor="middle"
                {...AXIS_TEXT}
                style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
              >
                {`${format(overflowBin.x0)}+`}
              </text>
            </g>
          )}
        </svg>
      )}
      {overflowBin && width > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: colors.grey[600] }}>
          Axis stops at {format(overflowBin.x0)} so the bulk stays readable
          — {overflowBin.count} deal{overflowBin.count === 1 ? '' : 's'} above that
          {overflowBin.count === 1 ? ' is' : ' are'} grouped in the final bar (highest {format(overflowBin.x1)}).
        </Typography>
      )}
    </Box>
  );
};

// --------------------------------------------------------------------------- //
// Bubble scatter — MOIC × IRR, area ∝ deal size
// --------------------------------------------------------------------------- //
export interface BubblePoint {
  id: string;
  label: string;
  x: number;             // MOIC
  y: number;             // IRR, percent
  size: number | null;   // acquisition price; null = unknown, drawn at min radius
  sub?: string;          // secondary tooltip line (model type · state)
}

const R_MIN = 5;
const R_MAX = 26;

export const BubbleScatter: React.FC<{
  points: BubblePoint[];
  height?: number;
  hue?: string;
  xLabel?: string;
  yLabel?: string;
  fmtX?: (n: number) => string;
  fmtY?: (n: number) => string;
  fmtSize?: (n: number) => string;
  sizeLabel?: string;
  ariaLabel?: string;
}> = ({
  points, height = 380, hue = colors.blue, ariaLabel,
  xLabel = 'Levered MOIC', yLabel = 'Levered IRR', sizeLabel = 'acquisition price',
  fmtX = fmtMoic, fmtY = fmtPctPoint, fmtSize = fmtMoney,
}) => {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<string | null>(null);

  const pad = { top: 16, right: 18, bottom: 46, left: 56 };
  const plotW = Math.max(0, width - pad.left - pad.right);
  const plotH = Math.max(0, height - pad.top - pad.bottom);

  const scales = useMemo(() => {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const build = (vals: number[]) => {
      const lo = Math.min(...vals);
      const hi = Math.max(...vals);
      const span = hi - lo || Math.abs(hi) || 1;
      const step = niceStep(span / 4);
      // one step of head/foot room so edge bubbles aren't clipped by the frame
      const min = Math.floor(lo / step) * step - (lo - Math.floor(lo / step) * step < step * 0.35 ? step : 0);
      const max = Math.ceil(hi / step) * step + (Math.ceil(hi / step) * step - hi < step * 0.35 ? step : 0);
      const ticks: number[] = [];
      for (let t = min; t <= max + 1e-9; t += step) ticks.push(Number(t.toFixed(6)));
      return { min, max, ticks };
    };
    const maxSize = Math.max(1, ...points.map((p) => p.size ?? 0));
    return { x: build(xs), y: build(ys), maxSize };
  }, [points]);

  if (!points.length) return null;

  const xOf = (v: number) => pad.left + ((v - scales.x.min) / (scales.x.max - scales.x.min)) * plotW;
  const yOf = (v: number) => pad.top + plotH - ((v - scales.y.min) / (scales.y.max - scales.y.min)) * plotH;
  // Area — not radius — carries size, so a 4× deal looks 4× as big.
  const rOf = (s: number | null) =>
    s == null ? R_MIN : R_MIN + Math.sqrt(s / scales.maxSize) * (R_MAX - R_MIN);

  const medX = median(points.map((p) => p.x));
  const medY = median(points.map((p) => p.y));

  // Big bubbles first so small ones are never buried underneath.
  const ordered = [...points].sort((a, b) => rOf(b.size) - rOf(a.size));

  const sizeLegend = (() => {
    const sizes = points.map((p) => p.size).filter((s): s is number => s != null).sort((a, b) => a - b);
    if (sizes.length < 2) return [];
    const pick = [sizes[0], sizes[Math.floor(sizes.length / 2)], sizes[sizes.length - 1]];
    return Array.from(new Set(pick));
  })();

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      {width > 0 && (
        <>
          <svg width={width} height={height} role="img" aria-label={ariaLabel} style={{ display: 'block', overflow: 'visible' }}>
            {/* grid */}
            {scales.y.ticks.map((t) => (
              <g key={`y${t}`}>
                <line x1={pad.left} x2={pad.left + plotW} y1={yOf(t)} y2={yOf(t)} stroke={colors.grey[300]} strokeWidth={1} />
                <text x={pad.left - 8} y={yOf(t) + 4} textAnchor="end" {...AXIS_TEXT} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtY(t)}
                </text>
              </g>
            ))}
            {scales.x.ticks.map((t) => (
              <g key={`x${t}`}>
                <line x1={xOf(t)} x2={xOf(t)} y1={pad.top} y2={pad.top + plotH} stroke={colors.grey[300]} strokeWidth={1} />
                <text x={xOf(t)} y={pad.top + plotH + 18} textAnchor="middle" {...AXIS_TEXT} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtX(t)}
                </text>
              </g>
            ))}

            {/* median crosshair — splits the plot into "better / worse than my book" */}
            {medX != null && (
              <line x1={xOf(medX)} x2={xOf(medX)} y1={pad.top} y2={pad.top + plotH} stroke={colors.grey[400]} strokeWidth={1} />
            )}
            {medY != null && (
              <line x1={pad.left} x2={pad.left + plotW} y1={yOf(medY)} y2={yOf(medY)} stroke={colors.grey[400]} strokeWidth={1} />
            )}
            {medX != null && (
              <text x={xOf(medX) + 5} y={pad.top + 11} {...AXIS_TEXT} style={{ fontSize: 10, fill: colors.grey[600] }}>
                median {fmtX(medX)}
              </text>
            )}
            {medY != null && (
              <text x={pad.left + plotW - 4} y={yOf(medY) - 5} textAnchor="end" {...AXIS_TEXT} style={{ fontSize: 10, fill: colors.grey[600] }}>
                median {fmtY(medY)}
              </text>
            )}

            {/* bubbles */}
            {ordered.map((p) => {
              const r = rOf(p.size);
              const active = hover === p.id;
              return (
                <Tooltip
                  key={p.id}
                  arrow
                  followCursor
                  title={
                    <span>
                      <strong>{p.label}</strong>
                      {p.sub ? <><br />{p.sub}</> : null}
                      <br />{`${xLabel}: ${fmtX(p.x)}`}
                      <br />{`${yLabel}: ${fmtY(p.y)}`}
                      <br />{p.size == null ? `${sizeLabel} not set` : `${sizeLabel}: ${fmtSize(p.size)}`}
                    </span>
                  }
                >
                  <g
                    onMouseEnter={() => setHover(p.id)}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: 'default' }}
                  >
                    {/* hit target ≥ 24px across, independent of the mark */}
                    <circle cx={xOf(p.x)} cy={yOf(p.y)} r={Math.max(12, r + 6)} fill="transparent" />
                    {/* 2px surface ring keeps overlapping bubbles legible */}
                    <circle cx={xOf(p.x)} cy={yOf(p.y)} r={r + 1} fill="none" stroke={colors.white} strokeWidth={2} />
                    <circle
                      cx={xOf(p.x)} cy={yOf(p.y)} r={r}
                      fill={alpha(hue, active ? 0.55 : 0.28)}
                      stroke={active ? colors.navy : colors.blueDark}
                      strokeWidth={active ? 2 : 1.25}
                      style={{ transition: 'fill .15s ease' }}
                    />
                  </g>
                </Tooltip>
              );
            })}

            {/* axis titles */}
            <text x={pad.left + plotW / 2} y={height - 4} textAnchor="middle" {...AXIS_TEXT} style={{ fontWeight: 600 }}>
              {xLabel}
            </text>
            <text
              transform={`translate(12, ${pad.top + plotH / 2}) rotate(-90)`}
              textAnchor="middle" {...AXIS_TEXT} style={{ fontWeight: 600 }}
            >
              {yLabel}
            </text>
          </svg>

          {/* size key — the third encoding needs its own scale, not a colour legend */}
          {sizeLegend.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: colors.grey[600] }}>Bubble size = {sizeLabel}</Typography>
              {sizeLegend.map((s) => (
                <Box key={s} sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
                  <svg width={rOf(s) * 2 + 4} height={rOf(s) * 2 + 4} style={{ display: 'block' }}>
                    <circle
                      cx={rOf(s) + 2} cy={rOf(s) + 2} r={rOf(s)}
                      fill={alpha(hue, 0.28)} stroke={colors.blueDark} strokeWidth={1.25}
                    />
                  </svg>
                  <Typography variant="caption" sx={{ color: colors.grey[700], fontVariantNumeric: 'tabular-nums' }}>
                    {fmtSize(s)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// --------------------------------------------------------------------------- //
// Filter bar
//
// ONE filter row above everything it scopes — never a filter inside a chart
// card, and never a per-chart filter. Every chart on the tab re-renders against
// the same slice, and the row states the slice in words ("Showing 14 of 38")
// so a thin chart is never mistaken for thin data.
//
// Chips carry the stage's own colour as a dot; the label stays ink. Colour
// follows the entity, so a stage keeps its colour whether or not it's selected.
// --------------------------------------------------------------------------- //
export const NO_STAGE = '__none__';

export interface StageOption { name: string; color: string | null }

export const StageFilterBar: React.FC<{
  stages: StageOption[];
  counts: Record<string, number>;
  selected: string[];
  onChange: (next: string[]) => void;
  shown: number;
  total: number;
}> = ({ stages, counts, selected, onChange, shown, total }) => {
  const active = selected.length > 0;
  const toggle = (name: string) =>
    onChange(selected.includes(name) ? selected.filter((s) => s !== name) : [...selected, name]);

  const options: StageOption[] = [...stages];
  if ((counts[NO_STAGE] ?? 0) > 0) options.push({ name: NO_STAGE, color: null });

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700, fontSize: '0.6875rem', color: colors.grey[600], mr: 0.5,
        }}
      >
        Stage
      </Typography>

      {options.map((s) => {
        const on = selected.includes(s.name);
        const count = counts[s.name] ?? 0;
        const label = s.name === NO_STAGE ? 'No stage' : s.name;
        return (
          <ButtonBase
            key={s.name}
            onClick={() => toggle(s.name)}
            aria-pressed={on}
            disabled={count === 0 && !on}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.25, py: 0.5, borderRadius: 5,
              border: `1px solid ${on ? colors.navy : colors.grey[300]}`,
              bgcolor: on ? colors.navy : colors.white,
              color: on ? colors.white : colors.grey[900],
              fontSize: '0.8125rem', fontWeight: 600,
              opacity: count === 0 && !on ? 0.45 : 1,
              transition: 'background-color .15s ease',
              '&:hover': { bgcolor: on ? colors.navy : colors.grey[50] },
            }}
          >
            <Box
              sx={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                bgcolor: s.color || colors.grey[400],
                boxShadow: on ? `0 0 0 1.5px ${colors.white}` : 'none',
              }}
            />
            {label}
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>
              {count}
            </Box>
          </ButtonBase>
        );
      })}

      <Box sx={{ flex: 1, minWidth: 8 }} />

      <Typography variant="body2" sx={{ color: colors.grey[600], whiteSpace: 'nowrap' }}>
        {active ? `Showing ${shown} of ${total} deals` : `All ${total} deals`}
      </Typography>
      {active && (
        <ButtonBase
          onClick={() => onChange([])}
          sx={{
            fontSize: '0.75rem', fontWeight: 700, color: colors.blueDark,
            px: 0.75, py: 0.5, borderRadius: 1,
            '&:hover': { bgcolor: colors.blueTint },
          }}
        >
          Clear
        </ButtonBase>
      )}
    </Paper>
  );
};

// --------------------------------------------------------------------------- //
// Metric catalogue helpers — shared by the configurable scatter and the
// comparison tab, so a metric lands in the same group in both places.
// --------------------------------------------------------------------------- //
export interface MetricMeta {
  key: string;
  unit: string;          // '%' | '$' | 'x' | ''
  count: number;         // deals with a numeric value
  non_negative: boolean; // legal as a bubble-size metric
}

const GROUPS: [string, (k: string) => boolean][] = [
  ['Returns', (k) => /^(Levered|Gross Project|Total (Profit|Equity|Capital)|Unlevered|Cash-on-Cash \(w\/)/.test(k)],
  ['Deal size', (k) => k.startsWith('Deal Size')],
  ['Going-in', (k) => k.startsWith('Going-in')],
  ['Year 1', (k) => k.startsWith('Year 1')],
  ['Stabilization', (k) => k.startsWith('At Stabilization') || k.startsWith('Stabilized')],
  ['Exit', (k) => /^(Exit|Cap Rate at Exit|Implied Valuation|Net Reversion|Selling Costs|Forward NOI|Retail:|Retail Exit|Mult?ifamily Exit)/.test(k)],
  ['Acquisition debt', (k) => k.startsWith('AQ:') || k.startsWith('Acquisition')],
  ['Refinancing', (k) => k.startsWith('Refi:') || k.startsWith('Perm ') || k.startsWith('Debt Metrics on Perm')],
  ['Construction & other debt', (k) => /^(Sr\. Cons|2L:|Pref|Floating|Fixed Rate|Interest Rate|Last Dollar|Participation|Origination|Senior Loan)/.test(k)],
  ['Timeline', (k) => k.startsWith('Timeline:') || k === 'Deal Time Horizon'],
];

export const groupFor = (key: string): string => {
  for (let i = 0; i < GROUPS.length; i += 1) if (GROUPS[i][1](key)) return GROUPS[i][0];
  return 'Other';
};
export const GROUP_ORDER: string[] = GROUPS.map((g) => g[0]).concat('Other');

/** Sort keys by group, then alphabetically — the order the dropdown reads in. */
export const byGroupThenName = (a: string, b: string): number => {
  const ga = GROUP_ORDER.indexOf(groupFor(a));
  const gb = GROUP_ORDER.indexOf(groupFor(b));
  return ga === gb ? a.localeCompare(b) : ga - gb;
};

/**
 * Axis formatter driven by the unit the sheet used, so a metric we've never
 * seen before still gets sensible ticks instead of raw floats.
 */
export const formatterFor = (unit: string) => (n: number): string => {
  if (unit === '%') return `${trim(n, 1)}%`;
  if (unit === 'x') return `${trim(n, 2)}x`;
  if (unit === '$') return fmtMoney(n);
  return trim(n, 2);
};
